import React from "react"
import {styled} from "@mui/material/styles"
import Typography from "@mui/material/Typography"
import {FALLBACK_FONT_FAMILY, breakpoints} from "@next/ux-fabric"
import {Grid as MuiGrid, Button as MUIButton} from "@mui/material"
import {PropsOf} from "@emotion/react"

export const NumFoundContainer = styled("div")`
    width: 100%;

    @media (min-width: ${breakpoints.values.md}px) {
        display: flex;
    }
`

export const HeaderContainer = styled("div")`
    align-items: center;
    border-top: 0.0625rem solid ${({theme}) => theme.uxfabric.components.card.cardHeader.default.borderColor};
    border-bottom: 1px solid ${({theme}) => theme.uxfabric.components.card.cardHeader.default.borderColor};
    color: ${({theme}) => theme.uxfabric.text.primary};
    background: ${({theme}) => theme.uxfabric.components.menu.searchMenu.default.backgroundColor};

    @media (max-width: ${breakpoints.values.md - 1}px) {
        padding: 0.75rem 1rem;
    }

    @media (min-width: ${breakpoints.values.md}px) {
        background: transparent;
        border: none;
    }
`

export type ContentContainerProps = {
    isNoResults?: boolean
    isRecentSearches: boolean
    showSelectionHighlight: boolean
}

export const ContentContainer = styled("div", {
    shouldForwardProp: p => p !== "isNoResults" && p !== "isRecentSearches" && p !== "showSelectionHighlight",
})<ContentContainerProps>`
    @media (min-width: ${breakpoints.values.md}px) {
        width: 25%;
        padding: ${({showSelectionHighlight}) => (showSelectionHighlight ? "1rem 0 1rem 0" : "1.5rem 0 1rem 1.5rem")};
        margin-left: ${({showSelectionHighlight}) => (showSelectionHighlight ? "0.4rem" : "0")};
        background: ${({theme}) => theme.uxfabric.components.menu.searchMenu.default.backgroundColor};
    }
    background: ${({theme}) => theme.uxfabric.components.menu.searchMenu.default.backgroundColor};
    width: 100%;
    height: auto;
    min-height: ${({isNoResults, isRecentSearches}) => (isNoResults || isRecentSearches ? "0rem" : "25.125rem")};
`

export const SecondaryContentContainer = styled("div")`
    max-width: 100%;
    width: 100%;
    padding: 1rem 0 1rem 1rem;
    height: 25.125rem;
    background: ${({theme}) => theme.uxfabric.components.menu.searchMenu.enriched.backgroundColor};

    @media (min-width: ${breakpoints.values.md}px) {
        max-width: 75%;
    }
`

export const HeaderWrapper = styled("div")`
    display: flex;
    justify-content: space-between;
`

export const CloseIcon = styled("img")`
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    cursor: pointer;
    height: 2.75rem;
    width: 2.75rem;
`

const StyledClearButton = styled(MUIButton)`
    && {
        color: ${({theme}) => theme.uxfabric.components.link.primary.enabled.textColor};
        background: none;
        font-family: ${({theme}) => `${theme.uxfabric.global.body3.fontFamily}, ${FALLBACK_FONT_FAMILY}`} !important;
        font-size: ${({theme}) => theme.uxfabric.global.body3.fontSize};
        font-weight: ${({theme}) => theme.uxfabric.global.body3.fontWeight};
        text-transform: ${({theme}) => theme.uxfabric.global.body3.textTransform};
        line-height: 1.43;
        padding: 0;
        justify-content: flex-end;
        border: 0;
        &:hover,
        &:active {
            background: inherit;
            border: 0;
        }

        &:focus {
            border: 0;
            border-radius: 0;
        }
        @media (min-width: ${breakpoints.values.md}px) {
            display: none;
        }
    }
`

export const StyledClearButtonDesktop = styled(StyledClearButton)`
    && {
        display: inline-flex;
        @media (max-width: ${breakpoints.values.md - 0.25}px) {
            display: none;
        }
    }
`

export const ClearButton = (props: PropsOf<typeof StyledClearButton>) => {
    return <StyledClearButton disableRipple {...props} />
}

export const ClearButtonDesktop = (props: PropsOf<typeof StyledClearButton>) => {
    return <StyledClearButtonDesktop disableRipple {...props} />
}

export const ClearButtonDesktopWrapper = styled("div")`
    width: 100%;
    text-align: end;
    padding-right: 1.5rem;
    padding-top: 1.5rem;
    background: ${({theme}) => theme.uxfabric.components.menu.searchMenu.default.backgroundColor};
`

export const Title = styled(Typography)`
    && {
        font-family: ${({theme}) =>
            `${theme.uxfabric.global.subtitle2.fontFamily}, ${FALLBACK_FONT_FAMILY}`} !important;
        font-weight: ${({theme}) => theme.uxfabric.global.subtitle2.fontWeight};
        font-size: ${({theme}) => theme.uxfabric.global.subtitle2.fontSize};
        letter-spacing: ${({theme}) => theme.uxfabric.global.subtitle2.letterSpacing};
    }
`

export type GridWrapperProps = {
    isRecentSearches: boolean
    showSelectionHighlight: boolean
}

export const GridWrapper = styled(MuiGrid, {
    shouldForwardProp: p => p !== "isRecentSearches" && p !== "showSelectionHighlight",
})<GridWrapperProps>`
    @media (min-width: ${breakpoints.values.md}px) {
        display: flex;
        padding-top: ${({isRecentSearches}) => (isRecentSearches ? "1rem" : "0")};
        padding-left: 0;
        padding-right: 0;
        padding-bottom: 0;
        border-bottom: none;
        ${({showSelectionHighlight, theme}) =>
            showSelectionHighlight
                ? `
                ul li a{
                    padding-left: 0.75rem;
                }
                ul li {
                    &:hover {
                        border-left: 0.25rem solid ${theme.uxfabric.primary.main};
                        border-top: 0.063rem solid ${theme.uxfabric.components.menu.searchMenu.default.borderColor};
                        border-bottom: 0.063rem solid ${theme.uxfabric.components.menu.searchMenu.default.borderColor};
                        border-right: 0.063rem solid ${theme.uxfabric.components.menu.searchMenu.enriched.backgroundColor};
                        a {
                            text-decoration: none;
                        }
                    }
                    
                }`
                : `ul li a {
                    transition: transform 0.1s ease, padding 0.15s ease;
                    &:hover {
                        padding-left: 0.5rem;
                    }
                }`}
    }
    padding: 0.75rem 1rem 0.25rem 1rem;
    border-bottom: ${({isRecentSearches, theme}) =>
        isRecentSearches ? "none" : `0.0625rem solid ${theme.uxfabric.components.menu.searchMenu.default.borderColor}`};

    @media (max-width: ${breakpoints.values.md - 1}px) {
        padding-top: 0.25rem;
    }

    div ul {
        flex-direction: column;
    }
`
